"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProfitAnalysis = exports.willHiddenTodayPnl = exports.getStatTimeRange = exports.isBrowser = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const StatisticsApi_1 = require("../../../apis/StatisticsApi");
const interface_1 = require("../../../interface");
const trade_utils_lib_1 = require("trade_utils_lib");
const FuturesPnl_1 = require("../../Account/FuturesPnl");
function getRecentRange(days) {
    const endTime = (0, dayjs_1.default)().startOf("day");
    return {
        beginTime: endTime.subtract(days, "day").startOf("day").unix(),
        endTime: endTime.unix(),
    };
}
const ONE_DAY = 24 * 60 * 60;
exports.isBrowser = typeof window !== "undefined" && typeof document !== "undefined";
function kLineDayRange({ beginTime, endTime }) {
    // 0时区的时候请求k线不用延后一天
    if (new Date().getTimezoneOffset() === 0) {
        return { beginTime, endTime };
    }
    return {
        beginTime: beginTime + ONE_DAY,
        endTime: exports.isBrowser ? endTime : endTime + ONE_DAY,
    };
}
function getStatTimeRange(range) {
    if (range === "7days") {
        return getRecentRange(7);
    }
    if (range === "30days") {
        return getRecentRange(30);
    }
    const beginTime = (0, dayjs_1.default)(range.beginTime).startOf("day").unix();
    let endTime = (0, dayjs_1.default)(range.endTime).add(1, "day").startOf("day").unix();
    return { beginTime, endTime };
}
exports.getStatTimeRange = getStatTimeRange;
const formatKLineData = (kLineData, beginTime) => {
    if (!kLineData.length || !beginTime) {
        return [];
    }
    const beginDay = (0, dayjs_1.default)(beginTime).format("YYYY-MM-DD");
    const startIndex = kLineData.findIndex((it) => (0, dayjs_1.default)(it.date * 1000).format("YYYY-MM-DD") === beginDay);
    if (startIndex < 0) {
        return [];
    }
    const realKlineData = kLineData.slice(startIndex);
    const { open } = realKlineData[0];
    const firstOpenPrice = new trade_utils_lib_1.SafeDecimal(open);
    return realKlineData.map((it) => (Object.assign(Object.assign({}, it), { rate: new trade_utils_lib_1.SafeDecimal(it.close).sub(firstOpenPrice).div(firstOpenPrice).toDP(4).toNumber() })));
};
/**每天的0点到1点隐藏今日盈亏（不包含1点） */
function willHiddenTodayPnl() {
    const updated_at = (0, dayjs_1.default)().valueOf();
    const begin = (0, dayjs_1.default)().startOf("day").valueOf();
    const end = (0, dayjs_1.default)().startOf("day").add(1, "hour").valueOf();
    return updated_at >= begin && updated_at < end;
}
exports.willHiddenTodayPnl = willHiddenTodayPnl;
function useProfitAnalysis(props) {
    const { timeRange = "7days", totalAsset } = props;
    const [balancesStatOri, setBalancesStatOri] = (0, react_1.useState)();
    const [range, setRange] = (0, react_1.useState)(timeRange);
    const lastSub = (0, react_1.useRef)();
    const [history, setHistory] = (0, react_1.useState)();
    const [kLineData, setKLineData] = (0, react_1.useState)();
    const [coinType, setCoinType] = (0, react_1.useState)(interface_1.BaseCoinType.BTC);
    const [loadingStat, setLoadingStat] = (0, react_1.useState)(true);
    const [loadingHistory, setLoadingHistory] = (0, react_1.useState)(false);
    const [hiddenTodayPnl, setHiddenTodayPnl] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        const sub = (0, StatisticsApi_1.getBalancesStat)().subscribe({
            next: (data) => {
                if (willHiddenTodayPnl()) {
                    setHiddenTodayPnl(true);
                }
                const updated_at = (0, dayjs_1.default)().valueOf();
                // 更新为本地时间
                if (data) {
                    setBalancesStatOri(Object.assign(Object.assign({}, data), { updated_at }));
                }
                setLoadingStat(false);
            },
            error: () => {
                setLoadingStat(false);
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, []);
    const balancesStat = (0, react_1.useMemo)(() => {
        if (!balancesStatOri || !totalAsset) {
            return;
        }
        const total = (0, FuturesPnl_1.calculateTotalFuturesPnl)({
            totalAsset: totalAsset,
            withdraw_deposit: balancesStatOri.withdraw_deposit,
            midnight_access: balancesStatOri.midnight_access,
        });
        return Object.assign(Object.assign({}, balancesStatOri), { today: total });
    }, [balancesStatOri, totalAsset]);
    const execFetchHistory = (0, react_1.useCallback)((rangeType) => {
        setLoadingHistory(true);
        const ranges = getStatTimeRange(rangeType);
        return (0, rxjs_1.zip)((0, StatisticsApi_1.getBalancesHistory)(ranges), (0, StatisticsApi_1.getKLineData)(coinType, kLineDayRange(ranges))).subscribe({
            next: ([history, kLineData]) => {
                var _a;
                setHistory(history);
                const kline = formatKLineData(kLineData.history_price, (_a = history.yield_items[0]) === null || _a === void 0 ? void 0 : _a.timestamp);
                setKLineData(kline);
                setLoadingHistory(false);
            },
            error: () => {
                setLoadingHistory(false);
            }
        });
    }, [coinType]);
    (0, react_1.useEffect)(() => {
        if (!range) {
            return;
        }
        const sub = execFetchHistory(range);
        return () => {
            sub.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const fetchBalancesHistory = (0, react_1.useCallback)((rangeType) => {
        if (lastSub.current) {
            lastSub.current.unsubscribe();
        }
        lastSub.current = execFetchHistory(rangeType);
    }, [execFetchHistory]);
    const changeStatTimeRange = (0, react_1.useCallback)((range) => {
        setRange(range);
        fetchBalancesHistory(range);
    }, [fetchBalancesHistory]);
    const beginTime = !!(history === null || history === void 0 ? void 0 : history.yield_items.length) ? history === null || history === void 0 ? void 0 : history.yield_items[0].timestamp : undefined;
    const changeCoinType = (0, react_1.useCallback)((coin) => {
        setCoinType(coin);
        // reload coin k line data
        (0, StatisticsApi_1.getKLineData)(coin, kLineDayRange(getStatTimeRange(range))).subscribe({
            next: (kLineData) => {
                const kline = formatKLineData(kLineData.history_price, beginTime);
                setKLineData(kline);
            },
            error: () => { }
        });
    }, [beginTime, range]);
    const shareData = (0, react_1.useMemo)(() => {
        if (!history) {
            return undefined;
        }
        const { yield_items = [], pnl_items = [], assets_items = [] } = history;
        let lastDayProfitRate = "";
        let lastDayAmountProfitRate = "";
        let lastDayProfit = "";
        let lastDayAssets = "";
        let beginTime = Date.now();
        let endTime = Date.now();
        if (yield_items.length) {
            const lastItem = yield_items[yield_items.length - 1];
            lastDayProfitRate = lastItem.daily_pnl_yield;
            lastDayAmountProfitRate = lastItem.total_pnl_yield;
            beginTime = yield_items[0].timestamp;
            endTime = lastItem.timestamp;
        }
        if (pnl_items.length) {
            const lastItem = pnl_items[pnl_items.length - 1];
            lastDayProfit = lastItem.amount;
        }
        if (assets_items.length) {
            const lastItem = assets_items[assets_items.length - 1];
            lastDayAssets = lastItem.amount;
        }
        return {
            lastDayProfitRate,
            lastDayAmountProfitRate,
            lastDayProfit,
            lastDayAssets,
            shareTimeRange: {
                beginTime,
                endTime,
            },
        };
    }, [history]);
    return {
        balancesStat,
        changeStatTimeRange,
        fetchBalancesHistory,
        history,
        loadingHistory,
        loadingStat,
        changeCoinType,
        coinType,
        kLineData,
        timeRange: range,
        shareData,
        hiddenTodayPnl,
    };
}
exports.useProfitAnalysis = useProfitAnalysis;
